import { useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { Link, useNavigate } from "react-router";
import { useCanEdit } from "@/api/session";
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  Icon,
  Kbd,
  Money,
  OdorPath,
  PageHeader,
  Quantity,
  Select,
  TextField,
  Toolbar,
  Tooltip,
  type DataTableColumn,
  type SelectOption,
} from "@/components";
import { useOdorTree } from "@/features/knowledge/api";
import { formatInteger, pluralize } from "@/lib/format";
import { useDebouncedValue, useHotkey, useSearchParamsInPlace } from "@/lib/hooks";
import {
  MATERIAL_KINDS,
  NOTE_POSITIONS,
  kindLabel,
  noteLabel,
  useMaterials,
  type MaterialKind,
  type MaterialListItem,
  type MaterialListParams,
  type MaterialSort,
  type NotePosition,
} from "./api";
import { CreateMaterialDialog } from "./CreateMaterialDialog";
import { dilutionText, packSizeText } from "./format";
import styles from "./MaterialsPage.module.css";

const PAGE_SIZE = 50;
const ALL = "all";

const SORT_OPTIONS: ReadonlyArray<SelectOption<MaterialSort>> = [
  { value: "name", label: "Name A–Z" },
  { value: "-name", label: "Name Z–A" },
  { value: "kind", label: "Kind" },
  { value: "note_position", label: "Note position" },
];

function isKind(value: string | null): value is MaterialKind {
  return MATERIAL_KINDS.some((kind) => kind.value === value);
}

function isNote(value: string | null): value is NotePosition {
  return NOTE_POSITIONS.some((note) => note.value === value);
}

function isSort(value: string | null): value is MaterialSort {
  return SORT_OPTIONS.some((option) => option.value === value);
}

/** Moves focus between row links with the arrow keys; Enter follows the focused link. */
function useRowKeyboard(container: RefObject<HTMLDivElement | null>, search: RefObject<HTMLInputElement | null>) {
  useEffect(() => {
    const element = container.current;
    const input = search.current;
    if (!element) return undefined;
    const links = () => Array.from(element.querySelectorAll<HTMLAnchorElement>("a[data-row-link]"));
    function onTableKey(event: KeyboardEvent) {
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      const all = links();
      const index = all.indexOf(document.activeElement as HTMLAnchorElement);
      if (index === -1) return;
      event.preventDefault();
      if (event.key === "ArrowUp" && index === 0) {
        input?.focus();
        return;
      }
      all[Math.min(all.length - 1, Math.max(0, index + (event.key === "ArrowDown" ? 1 : -1)))]?.focus();
    }
    function onSearchKey(event: KeyboardEvent) {
      if (event.key !== "ArrowDown") return;
      const first = links()[0];
      if (first) {
        event.preventDefault();
        first.focus();
      }
    }
    element.addEventListener("keydown", onTableKey);
    input?.addEventListener("keydown", onSearchKey);
    return () => {
      element.removeEventListener("keydown", onTableKey);
      input?.removeEventListener("keydown", onSearchKey);
    };
  }, [container, search]);
}

/** Every material the workspace can use: search by name, synonym, CAS or short code, then filter. */
export function MaterialsPage() {
  const [searchParams, setSearchParams] = useSearchParamsInPlace();
  const navigate = useNavigate();
  const canEdit = useCanEdit();
  const [createOpen, setCreateOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const q = searchParams.get("q") ?? "";
  const [searchText, setSearchText] = useState(q);
  const [syncedQ, setSyncedQ] = useState(q);
  if (q !== syncedQ) {
    // The URL changed from outside the field (Back, a link, "Clear"): show it.
    setSyncedQ(q);
    setSearchText(q);
  }
  const debouncedSearch = useDebouncedValue(searchText, 250);
  const kindParam = searchParams.get("kind");
  const noteParam = searchParams.get("note");
  const sortParam = searchParams.get("sort");
  const family = searchParams.get("family") ?? undefined;
  const owned = searchParams.get("owned");
  const offers = searchParams.get("offers");
  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);

  const params: MaterialListParams = {
    q: q || undefined,
    kind: isKind(kindParam) ? kindParam : undefined,
    note_position: isNote(noteParam) ? noteParam : undefined,
    odor_term: family,
    owned: owned === "yes" ? true : owned === "no" ? false : undefined,
    has_offers: offers === "yes" ? true : offers === "no" ? false : undefined,
    sort: isSort(sortParam) ? sortParam : undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  };
  const materials = useMaterials(params);
  const tree = useOdorTree();

  useRowKeyboard(tableRef, searchRef);
  useHotkey("/", () => searchRef.current?.focus());

  function update(changes: Record<string, string | null>, { resetPage = true } = {}) {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const [key, value] of Object.entries(changes)) {
          if (!value || value === ALL) next.delete(key);
          else next.set(key, value);
        }
        if (resetPage && !("page" in changes)) next.delete("page");
        return next;
      },
      { preventScrollReset: !("page" in changes) },
    );
  }

  useEffect(() => {
    const wanted = debouncedSearch.trim() ? debouncedSearch : "";
    if (debouncedSearch !== searchText || wanted === q) return;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (wanted) next.set("q", wanted);
        else next.delete("q");
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearch, searchText, q, setSearchParams]);

  const familyOptions = useMemo<SelectOption[]>(() => {
    const families = tree.data?.families ?? [];
    return [
      { value: ALL, label: "Any family" },
      ...families.map((node) => ({ value: node.id, label: node.name_en, detail: `${node.material_count} materials` })),
    ];
  }, [tree.data]);

  const columns: DataTableColumn<MaterialListItem>[] = [
    {
      id: "name",
      header: "Material",
      width: "wide",
      cell: (row) => {
        const secondary = [
          row.substance_name && row.substance_name !== row.name ? row.substance_name : null,
          row.grade || null,
        ].filter(Boolean);
        return (
          <span className={styles.nameCell}>
            <span className={styles.nameLine}>
              <Link to={`/materials/${row.id}`} className={styles.nameLink} data-row-link="">
                {row.name}
              </Link>
              {row.short_code ? <span className={styles.shortCode}>{row.short_code}</span> : null}
              {row.is_private ? <Badge tone="info">Private</Badge> : null}
            </span>
            {secondary.length ? <span className={styles.secondary}>{secondary.join(", ")}</span> : null}
          </span>
        );
      },
    },
    {
      id: "cas",
      header: "CAS",
      width: "fit",
      mono: true,
      cell: (row) =>
        row.cas ? (
          row.cas_relation === "broader" ? (
            <Tooltip content="Covers every grade of this natural; never resolved to a single molecule">
              <button type="button" className={styles.broaderCas}>
                {row.cas}
              </button>
            </Tooltip>
          ) : (
            row.cas
          )
        ) : (
          <span className={styles.missing}>—</span>
        ),
    },
    { id: "kind", header: "Kind", width: "fit", cell: (row) => kindLabel(row.kind) },
    {
      id: "note",
      header: "Note",
      width: "fit",
      cell: (row) => noteLabel(row.note_position) ?? <span className={styles.missing}>—</span>,
    },
    {
      id: "odor",
      header: "Odor family",
      cell: (row) =>
        row.odor_path.length ? (
          <OdorPath
            size="sm"
            terms={row.odor_path.slice(0, 3).map((term) => ({ id: term.id, label: term.name_en, family: term.swatch }))}
          />
        ) : (
          <span className={styles.missing}>—</span>
        ),
    },
    {
      id: "stock",
      header: "Stock",
      width: "fit",
      cell: (row) =>
        row.owned ? (
          <span className={styles.stockCell}>
            <Badge tone="success" icon="check">
              {pluralize(row.lot_count, "lot", "lots")}
            </Badge>
            <Quantity value={row.stock_neat_g} unit="g" />
          </span>
        ) : (
          <span className={styles.missing}>Not owned</span>
        ),
    },
    {
      id: "price",
      header: "Best price per neat g",
      numeric: true,
      width: "fit",
      cell: (row) => {
        const price = row.best_price;
        if (!price) return <span className={styles.missing}>{row.has_offers ? "No price" : "—"}</span>;
        const pack = [
          packSizeText(price.pack_g, price.pack_qty ?? null, price.pack_unit ?? null),
          dilutionText(price.dilution_fraction),
        ]
          .filter(Boolean)
          .join(" at ");
        return (
          <span className={styles.priceCell}>
            <span>
              {price.density_assumed ? "≈ " : null}
              <Money minor={price.price_per_neat_g_minor} currency={price.currency} extraDecimals={2} per="g" />
            </span>
            <span className={styles.secondary}>
              {price.supplier}, {pack}
              {price.in_stock === false ? ", out of stock" : ""}
              {price.density_assumed ? ", weighed at 1 g/mL" : ""}
            </span>
          </span>
        );
      },
    },
  ];

  const total = materials.data?.count ?? 0;
  const firstRow = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastRow = Math.min(total, page * PAGE_SIZE);
  const filtersActive = Boolean(q || params.kind || params.note_position || family || owned || offers);

  return (
    <div className={styles.page}>
      <PageHeader
        title="Materials"
        description="Molecules, naturals, bases and dilutions: what you own and what you could buy."
        meta={materials.data ? <Badge>{formatInteger(total)}</Badge> : null}
        actions={
          canEdit ? (
            <Button variant="primary" icon="plus" onClick={() => setCreateOpen(true)}>
              New material…
            </Button>
          ) : null
        }
      />

      <Toolbar label="Search and filter materials">
        <TextField
          label="Search"
          hideLabel
          type="search"
          inputRef={searchRef}
          className={styles.search}
          prefix={<Icon name="search" />}
          suffix={<Kbd keys={["/"]} />}
          placeholder="Name, synonym, CAS or short code"
          value={searchText}
          onValueChange={setSearchText}
          autoComplete="off"
          spellCheck={false}
          size="sm"
        />
        <Select
          label="Kind"
          size="sm"
          options={[{ value: ALL, label: "All kinds" }, ...MATERIAL_KINDS]}
          value={params.kind ?? ALL}
          onValueChange={(value) => update({ kind: value })}
        />
        <Select
          label="Note"
          size="sm"
          options={[{ value: ALL, label: "Any note" }, ...NOTE_POSITIONS]}
          value={params.note_position ?? ALL}
          onValueChange={(value) => update({ note: value })}
        />
        <Select
          label="Odor family"
          size="sm"
          options={familyOptions}
          value={family ?? ALL}
          onValueChange={(value) => update({ family: value })}
        />
        <Select
          label="Stock"
          size="sm"
          options={[
            { value: ALL, label: "Owned or not" },
            { value: "yes", label: "In stock" },
            { value: "no", label: "Not owned" },
          ]}
          value={owned ?? ALL}
          onValueChange={(value) => update({ owned: value })}
        />
        <Select
          label="Offers"
          size="sm"
          options={[
            { value: ALL, label: "Any" },
            { value: "yes", label: "Sold by a supplier" },
            { value: "no", label: "No offers" },
          ]}
          value={offers ?? ALL}
          onValueChange={(value) => update({ offers: value })}
        />
        <Select
          label="Sort"
          size="sm"
          options={SORT_OPTIONS}
          value={params.sort ?? "name"}
          onValueChange={(value) => update({ sort: value === "name" ? null : value }, { resetPage: false })}
        />
      </Toolbar>

      <div ref={tableRef}>
        <DataTable
          caption="Materials"
          columns={columns}
          rows={materials.data?.items}
          getRowId={(row) => row.id}
          loading={materials.isPending}
          error={materials.error}
          onRetry={() => void materials.refetch()}
          stickyHeader
          skeletonRows={10}
          empty={
            filtersActive ? (
              <EmptyState
                variant="inline"
                headingLevel={3}
                icon="search"
                title={q ? `No material matches “${q}”` : "No materials match these filters"}
                description="Search looks at names, synonyms, trade names, CAS numbers and short codes."
                actions={
                  <Button
                    onClick={() => {
                      setSearchText("");
                      setSearchParams({}, { replace: true });
                    }}
                  >
                    Clear search and filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                variant="inline"
                headingLevel={3}
                icon="book"
                title="No materials yet"
                description="Materials arrive with the research seed and supplier catalogs, or you can add your own."
              />
            )
          }
        />
      </div>

      {total > 0 ? (
        <nav className={styles.pager} aria-label="Pages">
          <span className={styles.pagerText}>
            {formatInteger(firstRow)}–{formatInteger(lastRow)} of {formatInteger(total)}
          </span>
          <Button
            size="sm"
            icon="chevronLeft"
            disabled={page <= 1}
            onClick={() => update({ page: page - 1 > 1 ? String(page - 1) : null }, { resetPage: false })}
          >
            Previous
          </Button>
          <Button
            size="sm"
            iconEnd="chevronRight"
            disabled={lastRow >= total}
            onClick={() => update({ page: String(page + 1) }, { resetPage: false })}
          >
            Next
          </Button>
        </nav>
      ) : null}

      <CreateMaterialDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        initialName={q}
        onCreated={(id) => void navigate(`/materials/${id}`)}
      />
    </div>
  );
}
